import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, ExternalLink, Inbox, Pencil, Plus, Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ActionIconButton } from "@/components/shared/action-icon-button";
import { DataTable } from "@/components/shared/data-table";
import { DataTableColumnHeader } from "@/components/shared/data-table-column-header";
import { EntityAuditPanel } from "@/components/shared/entity-audit-panel";
import { AppEntityLink } from "@/components/shared/entity-link";
import { IdDisplay } from "@/components/shared/id-display";
import {
  ArtifactTrustBadges,
  InstallStrategyBadge,
  InstallTrustBadges,
  InstallTrustReasonList,
} from "@/components/shared/security-signals";
import { StatusBadge } from "@/components/shared/status-badge";
import { TimeAgo } from "@/components/shared/time-ago";
import { Button } from "@/components/ui/button";
import { Empty, EmptyHeader, EmptyMedia, EmptyDescription } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  usePinRelease,
  useCreateReleaseArtifact,
  useRelease,
  useReleaseArtifacts,
  useReleaseObservations,
  useUnpinRelease,
  useUpdateRelease,
} from "@/hooks/use-releases";
import type { Artifact, ReleaseObservation } from "@/lib/types";
import {
  renderReleaseNotesHtml,
  renderReleaseNotesMarkdownHtml,
} from "@versioneer/core/pipeline/release-notes-render";

export const Route = createFileRoute("/releases/$releaseId")({
  component: ReleaseDetailPage,
});

const TARGET_ARCHITECTURES = ["arm64", "x86_64"] as const;

function ReleaseDetailPage() {
  const { releaseId } = Route.useParams();
  const { data: release, isLoading } = useRelease(releaseId);
  const updateRelease = useUpdateRelease(releaseId);
  const { data: artifactsData, isLoading: artifactsLoading } = useReleaseArtifacts(releaseId);
  const { data: observationsData, isLoading: observationsLoading } =
    useReleaseObservations(releaseId);
  const pinRelease = usePinRelease();
  const unpinRelease = useUnpinRelease();
  const createArtifact = useCreateReleaseArtifact(releaseId);
  const [artifactDraft, setArtifactDraft] = useState({
    artifactType: "dmg",
    architecture: "universal",
    url: "",
    sha256: "",
    sizeBytes: "",
    minOsVersion: "",
  });

  const artifactColumns = useMemo<ColumnDef<Artifact>[]>(
    () => [
      {
        accessorKey: "artifactType",
        meta: { label: "Type" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
            {row.original.artifactType}
          </span>
        ),
      },
      {
        accessorKey: "url",
        meta: { label: "Artifact" },
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Artifact" />,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-1">
            <a
              href={row.original.url}
              target="_blank"
              rel="noopener noreferrer"
              title={row.original.canonicalUrl}
              className="truncate font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
            >
              {displayArtifactUrl(row.original.canonicalUrl)}
            </a>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {row.original.sizeBytes ? (
                <span>{(row.original.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
              ) : null}
              {row.original.architecture ? <span>{row.original.architecture}</span> : null}
              {row.original.isPrimary ? <span>Primary</span> : null}
              {row.original.url !== row.original.canonicalUrl ? (
                <span>Signed query params hidden</span>
              ) : null}
              {row.original.sha256 ? (
                <span className="font-mono">sha256 {row.original.sha256.slice(0, 12)}...</span>
              ) : null}
              <ArtifactTrustBadges
                artifactType={row.original.artifactType}
                sha256={row.original.sha256}
              />
            </div>
          </div>
        ),
      },
      {
        id: "actions",
        meta: { label: "Actions" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <ActionIconButton label="Open artifact" icon={ExternalLink} asChild>
            <a href={row.original.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink data-icon="inline-start" />
            </a>
          </ActionIconButton>
        ),
      },
    ],
    [],
  );

  const observationColumns = useMemo<ColumnDef<ReleaseObservation>[]>(
    () => [
      {
        accessorKey: "observedVersionRaw",
        meta: { label: "Version" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Version" />,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-mono text-sm">{row.original.observedVersionRaw ?? "--"}</span>
            <span className="text-xs text-muted-foreground">
              {row.original.observedBuildNumber
                ? `Build ${row.original.observedBuildNumber}`
                : "--"}
            </span>
            {row.original.seenCount > 1 ? (
              <span className="text-xs text-muted-foreground">Seen {row.original.seenCount}x</span>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "observedChannel",
        meta: { label: "Channel" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Channel" />,
        cell: ({ row }) =>
          row.original.observedChannel ? (
            <StatusBadge status={row.original.observedChannel} />
          ) : (
            "--"
          ),
      },
      {
        accessorKey: "observedPublishedAt",
        meta: { label: "Published" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Published" />,
        cell: ({ row }) => <TimeAgo date={row.original.observedPublishedAt} />,
      },
      {
        accessorKey: "confidence",
        meta: { label: "Confidence" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Confidence" />,
        cell: ({ row }) => (row.original.confidence != null ? `${row.original.confidence}%` : "--"),
      },
      {
        accessorKey: "observedDownloadUrl",
        meta: { label: "Download" },
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Download" />,
        cell: ({ row }) =>
          row.original.observedDownloadUrl ? (
            <a
              href={row.original.observedDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={row.original.observedDownloadUrl}
              className="block max-w-64 truncate text-xs text-blue-600 hover:underline dark:text-blue-400"
            >
              {displayArtifactUrl(row.original.observedDownloadUrl)}
            </a>
          ) : (
            "--"
          ),
      },
      {
        accessorKey: "observedReleaseNotesUrl",
        meta: { label: "Notes" },
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Notes" />,
        cell: ({ row }) =>
          row.original.observedReleaseNotesUrl ? (
            <a
              href={row.original.observedReleaseNotesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
            >
              Release Notes <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            "--"
          ),
      },
      {
        accessorKey: "lastSeenAt",
        meta: { label: "Last Seen" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Last Seen" />,
        cell: ({ row }) => <TimeAgo date={row.original.lastSeenAt} />,
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-64" />
      </div>
    );
  }

  if (!release) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Inbox />
          </EmptyMedia>
          <EmptyDescription>Release not found.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div>
      <Link
        to="/releases"
        search={{ page: 1, pageSize: 50, channel: "all", status: "all" }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Releases
      </Link>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-mono text-xl font-semibold tracking-tight">{release.versionRaw}</h2>
            <StatusBadge status={release.channel} />
            <StatusBadge status={release.status} />
            {release.isPrerelease ? <StatusBadge status="beta" /> : null}
            {release.isLatestForChannel ? <StatusBadge status="latest" /> : null}
            {release.isPinnedLatest ? <StatusBadge status="pinned" /> : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <IdDisplay id={release.id} />
            {release.buildNumber ? <span>Build {release.buildNumber}</span> : null}
          </div>
          {release.app ? (
            <div className="mt-3">
              <AppEntityLink app={release.app} showId />
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {TARGET_ARCHITECTURES.map((targetArchitecture) => {
            const targetRow = release.latestTargets.find(
              (target) => target.targetArchitecture === targetArchitecture,
            );
            const isPinned = targetRow?.pinnedReleaseId === release.id;
            return (
              <Button
                key={targetArchitecture}
                variant="outline"
                size="sm"
                onClick={() => {
                  const mutation = isPinned ? unpinRelease : pinRelease;
                  mutation.mutate(
                    { releaseId: release.id, targetArchitecture },
                    {
                      onSuccess: () =>
                        toast.success(
                          isPinned
                            ? `${targetArchitecture} unpinned`
                            : `${targetArchitecture} pinned`,
                        ),
                      onError: (error) => toast.error(error.message),
                    },
                  );
                }}
              >
                {isPinned ? `Unpin ${targetArchitecture}` : `Pin ${targetArchitecture}`}
              </Button>
            );
          })}
          <Select
            value={release.status}
            onValueChange={(value) =>
              updateRelease.mutate(
                { status: value as "active" | "superseded" | "draft" | "withdrawn" },
                {
                  onSuccess: () => toast.success("Status updated"),
                  onError: (error) => toast.error(error.message),
                },
              )
            }
          >
            <SelectTrigger size="sm" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="superseded">Superseded</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="withdrawn">Withdrawn</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 rounded-lg border p-4">
        <dl className="grid gap-x-8 gap-y-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Normalized</dt>
            <dd className="mt-0.5 font-mono">{release.versionNormalized}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Released</dt>
            <dd className="mt-0.5">
              <TimeAgo date={release.releasedAt} />
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Confidence</dt>
            <dd className="mt-0.5">
              {release.sourceConfidence != null ? `${release.sourceConfidence}%` : "--"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Created</dt>
            <dd className="mt-0.5">
              <TimeAgo date={release.createdAt} />
            </dd>
          </div>
        </dl>
      </div>

      <ReleaseNotesSection
        releaseId={releaseId}
        releaseNotesMarkdown={release.releaseNotesMarkdown}
        releaseNotesHtml={release.releaseNotesMarkdown ? null : release.releaseNotesHtml}
        releaseNotesUrl={release.releaseNotesUrl}
      />

      <div className="mt-6 rounded-lg border p-4">
        <h3 className="text-lg font-medium">Install Trust</h3>
        {release.latestTargets.length > 0 ? (
          <div className="mt-3 flex flex-col gap-3">
            {release.latestTargets.map((target) => (
              <div
                key={target.id}
                className="grid gap-3 rounded-md border bg-muted/20 px-3 py-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
                    {target.targetArchitecture}
                  </span>
                  <InstallStrategyBadge
                    strategy={target.installStrategy}
                    reasons={target.trustWarnings}
                  />
                  {target.pinnedReleaseId ? <StatusBadge status="pinned" /> : null}
                  <InstallTrustBadges reasons={target.trustWarnings} />
                  {target.artifactId ? <IdDisplay id={target.artifactId} /> : null}
                </div>
                <InstallTrustReasonList reasons={target.trustWarnings} />
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            This release is not selected as the latest release for its channel.
          </p>
        )}
      </div>

      <div className="mt-6">
        <EntityAuditPanel targetType="release" targetId={releaseId} />
      </div>

      <div className="mt-6">
        <div className="mb-3">
          <h3 className="text-lg font-medium">Artifacts</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Verification state, trust metadata, and artifact shortcuts.
          </p>
        </div>
        <form
          className="mb-3 grid gap-2 rounded-lg border bg-muted/20 p-3 lg:grid-cols-[minmax(9rem,0.7fr)_minmax(8rem,0.6fr)_minmax(0,2fr)_minmax(8rem,0.6fr)_minmax(8rem,0.6fr)_minmax(8rem,0.6fr)_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            createArtifact.mutate(
              {
                artifactType: artifactDraft.artifactType as
                  | "zip"
                  | "dmg"
                  | "pkg"
                  | "appcast_enclosure"
                  | "mac_app_store"
                  | "other",
                architecture: artifactDraft.architecture as
                  | "arm64"
                  | "x86_64"
                  | "universal"
                  | "unknown",
                url: artifactDraft.url,
                sha256: artifactDraft.sha256 || null,
                sizeBytes: artifactDraft.sizeBytes ? Number(artifactDraft.sizeBytes) : null,
                minOsVersion: artifactDraft.minOsVersion || null,
              },
              {
                onSuccess: () => {
                  toast.success("Artifact saved");
                  setArtifactDraft({
                    artifactType: "dmg",
                    architecture: "universal",
                    url: "",
                    sha256: "",
                    sizeBytes: "",
                    minOsVersion: "",
                  });
                },
                onError: (error) => toast.error(error.message),
              },
            );
          }}
        >
          <Select
            value={artifactDraft.artifactType}
            onValueChange={(value) =>
              setArtifactDraft((draft) => ({ ...draft, artifactType: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dmg">DMG</SelectItem>
              <SelectItem value="zip">ZIP</SelectItem>
              <SelectItem value="pkg">PKG</SelectItem>
              <SelectItem value="appcast_enclosure">Appcast</SelectItem>
              <SelectItem value="mac_app_store">Mac App Store</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={artifactDraft.architecture}
            onValueChange={(value) =>
              setArtifactDraft((draft) => ({ ...draft, architecture: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="universal">Universal</SelectItem>
              <SelectItem value="arm64">arm64</SelectItem>
              <SelectItem value="x86_64">x86_64</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={artifactDraft.url}
            onChange={(event) =>
              setArtifactDraft((draft) => ({ ...draft, url: event.target.value }))
            }
            placeholder="Artifact URL"
            required
          />
          <Input
            value={artifactDraft.sha256}
            onChange={(event) =>
              setArtifactDraft((draft) => ({ ...draft, sha256: event.target.value }))
            }
            placeholder="SHA-256"
          />
          <Input
            value={artifactDraft.sizeBytes}
            onChange={(event) =>
              setArtifactDraft((draft) => ({ ...draft, sizeBytes: event.target.value }))
            }
            inputMode="numeric"
            placeholder="Size bytes"
          />
          <Input
            value={artifactDraft.minOsVersion}
            onChange={(event) =>
              setArtifactDraft((draft) => ({ ...draft, minOsVersion: event.target.value }))
            }
            placeholder="Min macOS"
          />
          <Button type="submit" disabled={createArtifact.isPending}>
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </form>
        <DataTable
          columns={artifactColumns}
          data={artifactsData?.items ?? []}
          isLoading={artifactsLoading}
          emptyMessage="No artifacts."
          enableColumnVisibility
        />
      </div>

      <div className="mt-6">
        <div className="mb-3">
          <h3 className="text-lg font-medium">Observations</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Parser observations with published timestamps, channels, and source links.
          </p>
        </div>
        <DataTable
          columns={observationColumns}
          data={observationsData?.items ?? []}
          isLoading={observationsLoading}
          emptyMessage="No observations."
          enableColumnVisibility
        />
      </div>
    </div>
  );
}

function displayArtifactUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return `${url.host}${url.pathname}${url.search}`;
  } catch {
    return rawUrl;
  }
}

const NOTES_PROSE_CLASSES =
  "rounded-lg border p-4 text-sm leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-1.5 [&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 dark:[&_a]:text-blue-400";

function ReleaseNotesSection({
  releaseId,
  releaseNotesMarkdown,
  releaseNotesHtml,
  releaseNotesUrl,
}: {
  releaseId: string;
  releaseNotesMarkdown: string | null;
  releaseNotesHtml: string | null;
  releaseNotesUrl: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draftMarkdown, setDraftMarkdown] = useState(releaseNotesMarkdown ?? "");
  const [draftUrl, setDraftUrl] = useState(releaseNotesUrl ?? "");
  const updateRelease = useUpdateRelease(releaseId);
  const renderedReleaseNotesHtml = useMemo(() => {
    if (releaseNotesMarkdown) {
      return renderReleaseNotesMarkdownHtml(releaseNotesMarkdown);
    }
    if (releaseNotesHtml) {
      return renderReleaseNotesHtml(releaseNotesHtml);
    }
    return null;
  }, [releaseNotesHtml, releaseNotesMarkdown]);

  const handleSave = () => {
    updateRelease.mutate(
      { releaseNotesMarkdown: draftMarkdown || null, releaseNotesUrl: draftUrl || null },
      {
        onSuccess: () => {
          toast.success("Release notes updated");
          setEditing(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium">Release Notes</h3>
          {!editing && releaseNotesUrl ? (
            <a
              href={releaseNotesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              View external <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraftMarkdown(releaseNotesMarkdown ?? "");
                setDraftUrl(releaseNotesUrl ?? "");
                setEditing(false);
              }}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateRelease.isPending}>
              <Save className="h-4 w-4" />
              {updateRelease.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>
      {editing ? (
        <div className="flex flex-col gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Release Notes URL
            </label>
            <Input
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              placeholder="https://example.com/changelog/v1.2.3"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Release Notes Markdown
            </label>
            <Textarea
              value={draftMarkdown}
              onChange={(e) => setDraftMarkdown(e.target.value)}
              rows={12}
              className="font-mono text-xs"
              placeholder={"## What's New\n\n- Fixed bugs\n- Improved performance"}
            />
          </div>
        </div>
      ) : renderedReleaseNotesHtml ? (
        <div
          className={NOTES_PROSE_CLASSES}
          dangerouslySetInnerHTML={{ __html: renderedReleaseNotesHtml }}
        />
      ) : (
        <p className="text-sm text-muted-foreground">No release notes.</p>
      )}
    </div>
  );
}
